import { useCallback, useEffect, useMemo, useState } from 'react';
import { Wallet, Zap } from 'lucide-react';
import { useDashboardUi } from '@/components/layout/UserLayout';
import { BillIconSelect } from '@/components/bills/BillIconSelect';
import { BillMeterTypeToggle } from '@/components/bills/BillMeterTypeToggle';
import { BillPaymentSummary } from '@/components/bills/BillPaymentSummary';
import { BillValidatedCard } from '@/components/bills/BillValidatedCard';
import {
  BILL_AMOUNT_PRESETS,
  billInputClass,
  billSelectedOptionClass,
  formatBillNaira,
} from '@/components/bills/billPaymentUtils';
import { BillPurchaseSuccessModal } from '@/components/receipts/BillPurchaseSuccessModal';
import { PinVerificationModal } from '@/components/user/PinVerificationModal';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { useUserAuth } from '@/context/UserAuthContext';
import {
  getDiscoAbbreviation,
  getDiscoFallbackColor,
  getDiscoLogoPath,
} from '@/lib/billPricingAssets';
import { billsService, getApiErrorMessage } from '@/services';
import type { BillPurchaseResult, ElectricityDisco, MeterValidation } from '@/types/bills';
import type { BillReceiptData } from '@/types/receipt';
import { cn } from '@/utils/cn';
import { notify } from '@/utils/notify';
import { formatReceiptDateTime } from '@/utils/receiptFormatters';

function FormLabel({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div className="mb-2">
      <label className="block text-sm font-medium text-gray-700">{children}</label>
      {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

export function ElectricityPage() {
  const { user, persistUser } = useUserAuth();
  const { wallet, loadWallet } = useDashboardUi();
  const profilePhone = user?.phone ?? '';

  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(true);

  const [discos, setDiscos] = useState<ElectricityDisco[]>([]);
  const [selectedDiscoId, setSelectedDiscoId] = useState('');
  const [meterNumber, setMeterNumber] = useState('');
  const [meterType, setMeterType] = useState<'prepaid' | 'postpaid'>('prepaid');

  const [validating, setValidating] = useState(false);
  const [meterValidation, setMeterValidation] = useState<MeterValidation | null>(null);

  const [selectedAmount, setSelectedAmount] = useState(2000);
  const [customAmount, setCustomAmount] = useState('');
  const [amountToPay, setAmountToPay] = useState<number | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const [showPinModal, setShowPinModal] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [successModal, setSuccessModal] = useState<BillReceiptData | null>(null);

  const walletBalance = wallet?.balance ?? user?.walletBalance ?? 0;
  const selectedDisco = useMemo(
    () => discos.find((d) => d.serviceId === selectedDiscoId) ?? null,
    [discos, selectedDiscoId],
  );

  const discoOptions = useMemo(
    () =>
      discos.map((disco) => ({
        value: disco.serviceId,
        label: disco.fullName,
        sublabel: getDiscoAbbreviation(disco.serviceId, disco.name),
        logoUrl: getDiscoLogoPath(disco.serviceId),
        fallbackColor: getDiscoFallbackColor(disco.serviceId),
        fallbackInitial: getDiscoAbbreviation(disco.serviceId, disco.name),
      })),
    [discos],
  );

  const faceValue = useMemo(() => {
    if (customAmount.trim()) {
      const parsed = Number(customAmount);
      return Number.isFinite(parsed) && parsed >= 100 ? Math.round(parsed) : null;
    }
    return selectedAmount;
  }, [customAmount, selectedAmount]);

  const minAmount = Math.max(meterValidation?.minPurchaseAmount ?? 1000, 1000);

  const loadDiscos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await billsService.getElectricityDiscos();
      const data = res.data;
      setDiscos(data?.discos ?? []);
      setConfigured(Boolean(data?.configured));
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load electricity providers'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadQuote = useCallback(async () => {
    if (!selectedDisco || !faceValue || !meterValidation) {
      setAmountToPay(null);
      return;
    }
    setQuoteLoading(true);
    try {
      const res = await billsService.quoteElectricity(selectedDisco.serviceId, faceValue);
      setAmountToPay(res.data?.quote?.amountToPay ?? faceValue);
    } catch {
      setAmountToPay(faceValue);
    } finally {
      setQuoteLoading(false);
    }
  }, [faceValue, meterValidation, selectedDisco]);

  useEffect(() => {
    void loadDiscos();
  }, [loadDiscos]);

  useEffect(() => {
    void loadQuote();
  }, [loadQuote]);

  useEffect(() => {
    setMeterValidation(null);
    setSuccessModal(null);
  }, [selectedDiscoId, meterNumber, meterType]);

  async function handleValidateMeter() {
    if (!selectedDisco) {
      notify.error('Select a distribution company first');
      return;
    }
    if (meterNumber.length < 10) {
      notify.error('Enter at least 10 digits for the meter number');
      return;
    }
    if (profilePhone.length < 11) {
      notify.error('Add a valid phone number in your profile before validating');
      return;
    }
    setValidating(true);
    try {
      const res = await billsService.validateElectricityMeter({
        serviceId: selectedDisco.serviceId,
        meterNumber,
        meterType,
      });
      setMeterValidation(res.data ?? null);
      notify.success('Meter validated');
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Meter validation failed'));
    } finally {
      setValidating(false);
    }
  }

  function handlePayClick() {
    if (profilePhone.length < 11) {
      notify.error('Add a valid phone number in your profile before paying');
      return;
    }
    setShowPinModal(true);
  }

  function buildElectricityReceipt(data: BillPurchaseResult): BillReceiptData {
    const { date, time } = formatReceiptDateTime();
    return {
      type: 'electricity',
      reference: data.reference,
      amount: faceValue!,
      amountPaid: data.amountPaid,
      providerName: selectedDisco!.fullName,
      date,
      time,
      meterNumber,
      meterType,
      customerName: meterValidation!.customerName,
      customerAddress: meterValidation!.address,
      token: data.token ?? null,
      units: data.units ? String(data.units) : null,
    };
  }

  async function handlePurchase(pin: string) {
    if (!selectedDisco || !faceValue || !meterValidation || amountToPay == null) return;
    setPurchasing(true);
    try {
      const res = await billsService.purchaseElectricity({
        serviceId: selectedDisco.serviceId,
        meterNumber,
        meterType,
        amount: faceValue,
        phone: profilePhone,
        pin,
      });
      const data = res.data;
      if (!data) return;
      if (data.user) persistUser({ ...user!, walletBalance: data.user.walletBalance });
      await loadWallet();
      setShowPinModal(false);
      setSuccessModal(buildElectricityReceipt(data));
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Purchase failed'));
    } finally {
      setPurchasing(false);
    }
  }

  const canPay =
    configured &&
    selectedDisco &&
    meterValidation &&
    faceValue != null &&
    faceValue >= minAmount &&
    amountToPay != null &&
    walletBalance >= amountToPay &&
    profilePhone.length >= 11;

  const summaryRows = [
    { label: 'Provider', value: selectedDisco?.fullName ?? 'Not selected' },
    { label: 'Meter type', value: meterType.charAt(0).toUpperCase() + meterType.slice(1) },
    { label: 'Meter number', value: meterNumber || '—' },
    ...(meterValidation ? [{ label: 'Customer', value: meterValidation.customerName }] : []),
    { label: 'Phone', value: profilePhone || 'Add in profile' },
    { label: 'Amount', value: faceValue ? formatBillNaira(faceValue) : '—' },
  ];

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader label="Loading electricity..." />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4 pb-28 md:pb-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Pay Electricity Bill</h1>
        <p className="text-sm text-gray-500">
          Choose provider and meter type, validate your meter, then pay from wallet.
        </p>
      </div>

      {!configured && (
        <Card className="border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Bill payment is not configured yet. Please contact support.
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="overflow-hidden border-gray-200 p-0 shadow-md">
          <div className="border-b border-gray-100 bg-gray-50 px-5 py-4">
            <h2 className="font-semibold text-gray-900">Payment details</h2>
            <p className="text-xs text-gray-500">Complete each step before paying</p>
          </div>
          <div className="space-y-5 p-5">
            <BillIconSelect
              label="Select electricity provider"
              placeholder="Choose a provider"
              sheetTitle="Electricity providers"
              value={selectedDiscoId}
              options={discoOptions}
              onChange={setSelectedDiscoId}
            />

            {selectedDisco && (
              <div>
                <FormLabel>Meter type</FormLabel>
                <BillMeterTypeToggle value={meterType} onChange={setMeterType} />
              </div>
            )}

            {selectedDisco && (
              <div>
                <FormLabel hint="Enter at least 10 digits, then tap Validate.">
                  Meter / account number
                </FormLabel>
                <div className="flex gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={meterNumber}
                    onChange={(e) => setMeterNumber(e.target.value.replace(/\D/g, ''))}
                    placeholder="Enter meter number"
                    className={cn(billInputClass, 'flex-1')}
                  />
                  {!meterValidation && meterNumber.length >= 10 && (
                    <Button
                      type="button"
                      size="sm"
                      className="shrink-0 px-4"
                      loading={validating}
                      onClick={() => void handleValidateMeter()}
                    >
                      Validate
                    </Button>
                  )}
                </div>
              </div>
            )}

            {meterValidation && (
              <BillValidatedCard title="Meter validated">
                <p className="font-medium">{meterValidation.customerName}</p>
                {meterValidation.address && <p>{meterValidation.address}</p>}
                <p className="text-green-700">Minimum purchase: {formatBillNaira(minAmount)}</p>
              </BillValidatedCard>
            )}

            <div className={cn('space-y-3', !meterValidation && 'pointer-events-none opacity-50')}>
              <FormLabel
                hint={!meterValidation ? 'Validate your meter to choose an amount.' : undefined}
              >
                Select amount
              </FormLabel>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {BILL_AMOUNT_PRESETS.map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    disabled={!meterValidation}
                    onClick={() => {
                      setSelectedAmount(amount);
                      setCustomAmount('');
                    }}
                    className={cn(
                      'rounded-xl border p-3 text-left transition',
                      !customAmount && selectedAmount === amount
                        ? billSelectedOptionClass
                        : 'border-gray-200 hover:border-secondary-300',
                    )}
                  >
                    <div className="text-sm font-semibold">{formatBillNaira(amount)}</div>
                    <div className="text-xs text-gray-500">Pay {formatBillNaira(amount)}</div>
                  </button>
                ))}
              </div>
              <input
                type="text"
                inputMode="numeric"
                disabled={!meterValidation}
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value.replace(/[^\d]/g, ''))}
                placeholder={`Custom amount (min ${formatBillNaira(minAmount)})`}
                className={billInputClass}
              />
            </div>

            <Button fullWidth disabled={!canPay} onClick={handlePayClick}>
              <Wallet className="h-4 w-4" />
              Pay {amountToPay ? formatBillNaira(amountToPay) : '—'} · Wallet{' '}
              {formatBillNaira(walletBalance)}
            </Button>
          </div>
        </Card>

        <div className="hidden md:block">
          <Card className="sticky top-24 border-gray-200 p-0 shadow-md">
            <div className="border-b border-gray-100 bg-gray-50 px-5 py-4">
              <div className="flex items-center gap-2 text-brand-700">
                <Zap className="h-5 w-5" />
                <div>
                  <h2 className="font-semibold text-gray-900">Payment summary</h2>
                  <p className="text-xs text-gray-500">Review before you pay</p>
                </div>
              </div>
            </div>
            <div className="space-y-4 p-5">
              <BillPaymentSummary
                rows={summaryRows}
                total={quoteLoading ? null : amountToPay}
                walletBalance={walletBalance}
                reversalNotice="Double-check your meter number. Electricity payments are instant and cannot be reversed."
              />
            </div>
          </Card>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-[4.5rem] z-40 border-t border-gray-200 bg-white/95 p-3 backdrop-blur md:hidden">
        <Button fullWidth disabled={!canPay} onClick={handlePayClick}>
          Pay {amountToPay ? formatBillNaira(amountToPay) : '—'} · Wallet{' '}
          {formatBillNaira(walletBalance)}
        </Button>
      </div>

      <PinVerificationModal
        open={showPinModal}
        onClose={() => setShowPinModal(false)}
        onConfirm={(pin) => void handlePurchase(pin)}
        loading={purchasing}
        title="Confirm electricity payment"
      />

      {successModal && (
        <BillPurchaseSuccessModal
          open
          onClose={() => setSuccessModal(null)}
          receipt={successModal}
        />
      )}
    </div>
  );
}
